import type { Dayjs } from 'dayjs';
import type { RegisterOptions } from 'react-hook-form';

import type { components } from '@/types/openapi';

import type {
  LimaDetailedSourceDTO,
  LogTypeStatusDataModel
} from '../DevicesTable/DevicesTable.types';
import type {
  DataProviderProps,
  DataProvidersModel
} from './DataProvider/DataProvider.types';
import SOURCE_TYPE from './SourceCreate.constants';

// =============================================================================
// Dialog & UI Types
// =============================================================================

/**
 * Modal operation modes for the source creation/management dialog.
 *
 * - New: Creating a new source from scratch
 * - Edit: Modifying an existing source's configuration
 * - Delete: Confirming deletion of a source
 * - AddThumbPrintId: Microsoft certificate authentication - adding thumbprint
 * - AddAccessCode: OAuth flow - adding authorization code
 */
export enum SourceDialogMode {
  New = 'New',
  Edit = 'Edit',
  Delete = 'Delete',
  AddThumbPrintId = 'AddThumbPrintId',
  AddAccessCode = 'AddAccessCode'
}

export type MessageType = 'success' | 'warning' | 'error' | 'info' | 'default';

// =============================================================================
// Backend OpenAPI DTOs
// =============================================================================

/** Basic source type info (id, name, cloud provider) */
export type BasicSourceTypeDTO = components['schemas']['SourceTypeBasic'];

/** Detailed source type with full connection schema and configuration */
export type DetailedSourceTypeDTO = components['schemas']['SourceTypeDetailed'];

/**
 * Source log type (category) - represents a specific data stream.
 * Examples: "Audit.AzureActiveDirectory", "Audit.Exchange", "DLP.All"
 */
export type BasicSourceLogTypeDTO = components['schemas']['SourceLogType'];

/** Base payload for creating a new source */
export type SourceCreateBaseDTO = components['schemas']['SourceCreateBase'];

/** Device details returned from Lima API */
export type LimaDevicesDTO = components['schemas']['LimaDeviceDetails'];

/**
 * Microsoft data provider configurations.
 * Used for O365, Defender, and Entra ID sub-sources.
 */
export type MicrosoftDataProviderDTO =
  components['schemas']['MicrosoftDataProvider'][];

/** On-demand vulnerability scan request for Medigate sources */
export type MedigateOndemandIngestionDTO =
  components['schemas']['MedigateOndemandIngestion'];

/** Response after successfully creating or updating a source */
export type SourceCreateResponseDTO =
  components['schemas']['SourceCreateResponse'];

// =============================================================================
// Action Payloads & Simple Models
// =============================================================================

/** Payload for bulk activating/deactivating sources */
export type ToggleActiveStatePayload = {
  source_uids: string[];
  active: boolean;
};

/** Basic source identifier response */
export type SourceResponse = {
  source_id?: number | null;
  source_uid?: string | null;
  source_name: string;
};

/**
 * Request to resubmit custom object data for a Salesforce source.
 * Used when custom object ingestion fails and needs retry.
 */
export type ResubmitCustomObjectRequest = {
  source_uid: string;
  custom_object: string;
};

/** Source log type with UI selection state */
export type SourceLogTypeModel = BasicSourceLogTypeDTO & {
  selected?: boolean;
};

/**
 * Alert field mapping for device-specific fields.
 * Used by sources that support custom alert field extraction.
 */
export type AlertDeviceField = {
  key: string;
  category: string;
};

// =============================================================================
// Form Models
// =============================================================================

/**
 * Complete form state for creating/editing a source.
 * This is the "kitchen sink" type that includes all possible fields
 * across all source types. Most fields are optional because different
 * source types require different configurations.
 *
 * Key field groups:
 * - Core: device_uid, source_type_id, organization_id, source_name
 * - Certificate auth: cert_data, cert_expiry_date, thumbprint_id
 * - Connection: All the provider-specific connection fields (client_id, etc.)
 * - Categories: category, categories, newly_added_category
 * - Data providers: For Microsoft and AWS S3 hierarchical sources
 */
export type SourceCreateDynamicFormModel = {
  id?: number | null;
  device_id?: number;
  device_uid?: string;
  source_id?: number;
  source_uid?: string;
  sub_source_type?: string;
  is_api_v2?: boolean;
  queue_setup_enabled?: boolean;
  name?: string;
  queue_name?: string;
  api_endpoint_type?: string;
  is_queue_name_changed?: boolean;
  source_type_id?: number;
  organization_id?: number;
  org_id?: number;
  source_type_name?: string;
  source_name?: string;
  site_name?: string;
  ip_address?: string | null;
  last_seen?: string | null;
  device_name?: string;
  status?: string | 'active' | 'inactive';
  active?: boolean | null;
  edit_status?: boolean | null;

  // Certificate authentication flow (Azure, Salesforce, etc.)
  is_certificate_flow?: boolean | null;
  is_new_certificate_flow?: boolean | null;
  cert_data?: ConnectionInputs;
  cert_details_obj?: unknown;
  cert_expiry_date?: Dayjs | null;
  cert_regenerate?: boolean | null;
  is_file_update?: boolean | null;
  file_name?: string | null;
  file_content?: string | null;

  // OAuth2 flow fields
  authorize_url?: string | null;
  thumbprint_id?: string | null; // Microsoft certificate thumbprint
  existing_thumbprint_id?: string | null;
  access_code?: string | null; // OAuth authorization code

  // Category management
  category?: string[];
  categories?: Category[];
  new_categories?: unknown;
  newly_added_category?: string[];
  removed_categories?: string[];

  // Secrets and file uploads
  is_secret_update?: boolean | null;
  cert_zip_data?: string | null;
  file_upload?: Blob;
  public_key?: Blob;
  private_key?: Blob;

  // Ingestion configuration
  run_ingestion?: boolean | null;
  expires?: Dayjs | null;
  enable_ingest_from_on_edit?: boolean | null;
  certificate_expires?: Dayjs | null;
  backup_expires?: Dayjs | null;
  ingest_from?: string;
  time_interval?: string;

  // Azure-specific fields
  sas_token?: string;
  contact_email?: string;
  container_name?: string;
  event_hub_name?: string;
  consumer_grp_name?: string;
  service_detail_name?: string;
  event_hub_conn_string?: string;
  storage_acct_conn_string?: string;

  // API configuration
  base_url?: string;
  api_token?: string;
  api_url?: string;

  // Vulnerability scanning (Tenable, Qualys, etc.)
  fetch_device_vuln?: boolean;
  vuln_scan_time_interval?: string;
  vuln_api_count?: number;
  vuln_event_count?: number;

  // Salesforce custom objects
  custom_objects?: string;
  custom_object_ingest_from?: string;
  fetch_custom_object?: boolean;
  custom_object_time_interval?: string;

  // Authentication credentials
  client_key?: string;
  client_secret?: string;
  admin_email?: string;
  app_id?: string;
  app_key?: string;
  access_key?: string; // AWS access key
  secret_key?: string; // AWS secret key
  subscription_id?: string; // Azure
  resource_group?: string; // Azure
  workspace_name?: string; // Azure Log Analytics
  client_id?: string; // OAuth/Azure AD
  tenant_id?: string; // Azure AD

  // Cloud provider selections
  newly_added_cloud_provider?: string[];

  // Device-specific configurations
  device_alert_fields?: AlertDeviceField[];

  // Hierarchical sources (Microsoft, S3)
  data_provider?: DataProvidersModel;
  data_provider_selection?: string;

  // Organization context
  organization_name?: string;
  site_name_prefix?: string;
};

/** Downloaded certificate bundle for client-side storage */
export type DownloadCertificateModel = {
  file_name?: string | null;
  file_content?: string | null;
};

/**
 * OAuth access code model.
 * Used after user completes OAuth flow and returns with authorization code.
 */
export type AccessCodeModel = {
  access_code?: string;
  source_name?: string;
  source_id?: number;
  source_uid?: string;
};

/**
 * Microsoft data provider configuration.
 * Represents a sub-source under a parent Microsoft source.
 * Examples: O365 Audit, Defender Alerts, Entra ID Sign-ins
 */
export type MicrosoftProviderConfig = {
  source_type_id?: number;
  organization_id?: number;
  source_uid?: string;
  source_name?: string;
  site_name?: string;
  active?: boolean;
  connection_inputs?: ConnectionInputs;
};

/**
 * Complete source creation/update request payload.
 * Sent to the backend API when creating or modifying a source.
 */
export type SourceCreateRequest = {
  id?: number | null;
  source_uid?: string | null;
  device_uid?: string | null;
  status_code?: number;
  is_api_v2?: boolean;
  queue_setup_enabled?: boolean;
  source_type_id: number;
  organization_id?: number;
  source_name: string;
  site_name?: string | null;
  ip_address?: string | null;
  last_seen?: string | null;
  connection_inputs: ConnectionInputs; // Provider-specific configuration
  source_id?: number | null;
  status?: string | null;
  active?: boolean | null;
  edit_status?: boolean | null;

  // Certificate flow fields
  is_certificate_flow?: boolean | null;
  is_new_certificate_flow?: boolean | null;
  cert_data?: ConnectionInputs;
  cert_details_obj?: unknown;
  cert_expiry_date?: string | null;
  cert_regenerate?: boolean | null;
  is_file_update?: boolean | null;
  file_name?: string | null;
  file_content?: string | null;

  // OAuth and API configuration
  authorize_url?: string | null;
  thumbprint_id?: string | null;
  access_code?: string | null;

  // Category and state management
  new_categories?: unknown;
  newly_added_category?: string[];
  newly_added_cloud_provider?: string[];
  removed_categories?: string[];

  // Security and file handling
  is_secret_update?: boolean | null;
  cert_zip_data?: string | null;
  run_ingestion?: boolean | null;
};

/**
 * Detailed source response from the backend.
 * Includes full configuration, schema, and metadata.
 */
export type SourceDetailResponse = {
  source_uid: string;
  type: string;
  name: string;
  source_name: string;
  site_name?: string;
  is_api_v2?: boolean;
  queue_setup_enabled?: boolean;
  queue_name?: string;
  api_endpoint_type?: string;
  authentication_method?: string;
  source_statuses?: components['schemas']['LimaSourceStatus'][] | null;
  sub_source_type?: string;
  device_uid?: string;
  organization_id?: number;
  source_type_id: number;
  last_seen?: string;
  ip_address?: string;
  active?: boolean;
  edit_status?: boolean;

  // Certificate configuration
  file_name?: string;
  file_content?: string;
  is_certificate_flow?: boolean;
  is_new_certificate_flow?: boolean;
  expires?: Dayjs | null;
  backup_expires?: Dayjs | null;
  cert_based_service?: boolean;

  // Categories and regions
  category: string[];
  categories?: Category[];
  region?: string[];
  cloud_provider?: string[];
  show_categories?: boolean;
  mandatory_categories?: boolean;
  show_regions?: boolean;

  // Time intervals
  enable_ingest_from_on_edit?: boolean;
  sub_time_interval?: string;
  time_interval?: string;

  // Dynamic form fields by authentication method
  source_inputs?: DynamicFieldData[];
  source_inputs_encoded?: string;
  client_secret_inputs?: DynamicFieldData[];
  client_secret_inputs_encoded?: string;
  new_certificate_inputs?: DynamicFieldData[];
  existing_certificate_inputs?: DynamicFieldData[];

  // Vulnerability scanning
  fetch_device_vuln?: boolean;
  vuln_scan_time_interval?: string;
  vuln_api_count?: number;
  vuln_event_count?: number;
  on_demand_vuln_scan?: boolean;
  vuln_fields?: string[];

  // Salesforce custom objects
  custom_objects?: string;
  custom_object_ingest_from?: string;
  fetch_custom_object?: boolean;
  custom_object_time_interval?: string;

  // Metadata and notes
  notes?: string[];
  fetch_device_vulnerabilities?: DynamicFieldData[];
  fetch_custom_object_events?: DynamicFieldData[];

  // Alert field configuration
  device_alert_fields?: AlertDeviceField[];
  alert_fields?: string[];
  alert_device_fields?: string[];

  // Audit fields
  created_at?: string;
  updated_at?: string;
  created_by?: string;
  updated_by?: string;

  // Hierarchical sources (Microsoft, S3)
  data_providers: DataSourceResponse[];
  s3_data_providers: DataSourceResponse[];
  ms_data_providers: DataSourceResponse[];
};

// =============================================================================
// Data Provider Types (Hierarchical Sources)
// =============================================================================

export type DataSourceRowProps = {
  DataProvider: React.ComponentType<DataProviderProps>;
  parent_name?: string;
  data_source: DataSourceModel;
};

export type DataSourceModel = {
  data_source: string;
  data_source_label: string;
  selected?: boolean;
  data_sources?: DataSourceModel[];
  source_inputs?: DynamicFieldData[];
  source_type_id?: number;
};

export type DataSourceLogType = {
  type: string;
  path?: string;
};

export type SourceResponseDTO = components['schemas']['SourceResponse'];

export type DataSourceResponse = SourceResponseDTO & {
  name?: string;
  prefix?: string; // S3 prefix
  ingest_from?: string; // Start date for data ingestion
  log_type?: string[]; // Selected log categories
  directory_path?: string; // File path for local/network sources
  connection_inputs?: ConnectionInputs | undefined;
};

// =============================================================================
// Schema & Dynamic Form Configuration
// =============================================================================

/**
 * Connection input schema that defines the dynamic form fields.
 * Each source type has a unique schema that determines which fields to show.
 *
 * Key concepts:
 * - client_secret_inputs: Form fields for OAuth2 client secret method
 * - new_certificate_inputs: Form fields for creating new certificate
 * - existing_certificate_inputs: Form fields for using existing certificate
 * - show_categories/show_regions: Boolean flags to control field visibility
 * - data_sources/data_providers: For hierarchical source configuration
 */
export type ConnectionInputSchema = {
  // Category configuration
  show_categories?: boolean;
  mandatory_categories?: boolean;
  show_select_all_in_categories?: boolean;

  // Authentication flow selection
  cert_based_service?: boolean;

  // Form field groups by authentication method
  source_inputs?: DynamicFieldData[];
  client_secret_inputs?: DynamicFieldData[];
  new_certificate_inputs?: DynamicFieldData[];
  existing_certificate_inputs?: DynamicFieldData[];

  // Informational messages
  notes?: string[];

  // Feature toggles
  fetch_device_vulnerabilities?: DynamicFieldData[];
  fetch_custom_object_events?: DynamicFieldData[];

  // Region/cloud provider selection
  show_regions?: boolean;
  region_inputs?: DynamicFieldData[];
  show_cloud_provider?: boolean;
  cloud_provider_inputs?: DynamicFieldData[];

  // Alert field mapping
  device_alert_fields?: DynamicFieldData;

  // Time interval configuration
  show_sub_time_interval?: boolean;
  sub_time_interval_inputs?: DynamicFieldData[];

  // API version selection
  is_api_v2?: boolean;
  source_inputs_v1?: DynamicFieldData[];
  source_inputs_v2?: DynamicFieldData[];

  // Sub-source type (for Microsoft)
  show_sub_source_type?: boolean;
  sub_source_type_inputs?: DynamicFieldData[];
  sub_source_type?: string;

  // Queue configuration
  queue_setup_enabled?: boolean;
  source_inputs_new_queue?: DynamicFieldData[];
  source_inputs_existing_queue?: DynamicFieldData[];

  // Container ingestion (Azure)
  is_container_ingest_enabled?: boolean;

  // Hierarchical source configuration
  data_sources?: DataSourceModel[];
  data_providers?: DataSourceResponse[];
};

/**
 * Dynamic field metadata for rendering form inputs.
 * Includes validation rules, options, and display configuration.
 */
export type DynamicFieldData = {
  id: number;
  name: string; // Field identifier (matches ConnectionInputs key)
  type: string; // Input type: text, password, select, date, checkbox, etc.
  label: string; // Display label for the UI
  value: string | string[] | null;
  defaultValue?: string | string[] | null;
  disabled?: boolean;
  validations: Validation[]; // Validation rules
  validation_type: string;
  options?: string[]; // For select/dropdown inputs
  retention?: number; // Data retention period in days
  msg?: string; // Help text or error message
  nonMandatory?: boolean; // Whether field is optional
  min?: number; // Minimum value for numeric inputs
  config?: RegisterOptions; // React Hook Form validation config
  time_interval?: string;
  note?: string; // Additional instructions
  source_type_id?: number;
  softExpiry?: boolean; // Whether to show soft expiry warning vs hard error
  showSelectAll?: boolean;
  onFieldChange?: (value: unknown[]) => void;
};

/** Validation rule configuration */
export type Validation = {
  type: string;
  rules?: Rule[];
  params?: string[];
  function_type?: FunctionType;
};

/** Individual validation rule */
export type Rule = {
  type: string;
  message: string;
  value?: string[];
};

/** Function-based validation */
export type FunctionType = {
  type: string;
  message: string;
};

export type CallbackFunction = () => void;

/**
 * Connection inputs - the actual values for a configured source.
 * This is a union of all possible fields across all source types.
 * Most fields are optional since different sources use different subsets.
 */
export type ConnectionInputs = {
  name?: string;
  expires?: Dayjs | string | null;
  backup_expires?: Dayjs | string | null;
  device_id?: number;
  sas_token?: string;
  ip_address?: string;
  contact_email?: string;
  container_name?: string;
  event_hub_name?: string;
  source_name?: string;
  source_type_id?: number;
  consumer_grp_name?: string;
  service_detail_name?: string;
  event_hub_conn_string?: string;
  storage_acct_conn_string?: string;
  base_url?: string;
  category?: string[];
  api_token?: string;
  categories?: Category[];
  ingest_from?: Dayjs | string | null;
  time_interval?: string;
  fetch_device_vuln?: boolean;
  vuln_scan_time_interval?: string;
  client_key?: string;
  access_code?: string;
  authorize_url?: string;
  client_secret?: string;
  file_name?: string;
  file_content?: string;
  file_upload?: Blob;
  admin_email?: string;
  status?: string;
  last_seen?: string;
  is_certificate_flow?: boolean;
  is_new_certificate_flow?: boolean;
  public_key?: string;
  private_key?: string;
  private_key_file_name?: string;
  public_key_file_name?: string;
  show_regions?: boolean;
  region?: string[];
  subscription_type?: string;
  api_url?: string;
  api_endpoint_type?: string;
  data_providers?: MicrosoftProviderConfig[] | Record<string, unknown>;
  s3_data_providers?:
    | DataSourceResponse[]
    | Array<{ name: string } & Record<string, unknown>>;
  // Additional fields for various source types
  is_api_v2?: boolean;
  queue_setup_enabled?: boolean;
  name_changed?: boolean;
  authentication_method?: string;
  thumbprint_id?: string;
  account_name?: string;
  id?: string;
};

/**
 * Extended Lima source model with status tracking.
 * Includes custom object sync status, event availability, and failed categories.
 */
export type LimaDetailedSourceModel = LimaDetailedSourceDTO & {
  custom_object_status?: LogTypeStatusDataModel[] | undefined;
  events_availability_history?: LogTypeStatusDataModel[] | undefined;
  failed_categories?: LogTypeStatusDataModel[] | undefined;
  connection_inputs?: ConnectionInputs | undefined;
};

// =============================================================================
// Category Management
// =============================================================================

/**
 * Category (log type) definition.
 * Represents a data stream type like "Audit.Exchange" or "DLP.All".
 * Categories are filtered by source type and API provider.
 */
export type Category = {
  id: number;
  name: string;
  source_type_id: number;
};

/** Props for rendering category selection field */
export type CategoryFieldProps = {
  provider: string;
  subProvider?: string;
  sourceTypeId: number;
  field: DynamicFieldData;
  initialValue?: boolean | null;
  mode?: SourceDialogMode;
};

// =============================================================================
// Constants & Mappings
// =============================================================================

/**
 * Maps Microsoft provider names to their source type IDs.
 * Used for category filtering and provider validation.
 */
export const PROVIDER_TYPE_MAP: Record<string, number> = {
  O365: 4,
  MicrosoftDefenderUnified: 15,
  GraphAPI: 36,
  O365API: 36
};

/**
 * Microsoft child source types that can be created as sub-sources.
 * These sources require a parent Microsoft authentication source.
 */
export const MICROSOFT_CHILD_SOURCE_TYPES = [
  SOURCE_TYPE.O365,
  SOURCE_TYPE.MICROSOFT_DEFENDER,
  SOURCE_TYPE.MICROSOFT_ENTRA_ID
] as const;

/**
 * Source types that support "soft expiry" warnings.
 *
 * Business rule: These sources show a warning (yellow) when credentials
 * are near expiration, rather than immediately failing (red).
 * This gives customers time to renew credentials before data collection stops.
 *
 * Typically applies to OAuth2 sources with refresh tokens.
 */
export const SOFT_EXPIRY_SOURCE_TYPES = new Set<number>([
  SOURCE_TYPE.DROPBOX,
  SOURCE_TYPE.MIMECAST,
  SOURCE_TYPE.SALESFORCE,
  SOURCE_TYPE.GOOGLE_WORKSPACE,
  SOURCE_TYPE.TRELLIX,
  SOURCE_TYPE.AWS_CLOUDTRAIL,
  SOURCE_TYPE.OKTA,
  SOURCE_TYPE.ABNORMAL_SECURITY,
  SOURCE_TYPE.PROOFPOINT_TAP,
  SOURCE_TYPE.CISCO_DUO,
  SOURCE_TYPE.PROOFPOINT_POD,
  SOURCE_TYPE.CROWD_STRIKE,
  SOURCE_TYPE.AWS_SECURITY_HUB,
  SOURCE_TYPE.NET_SUITE,
  SOURCE_TYPE.JUNIPER_MIST,
  SOURCE_TYPE.CARBON_BLACK,
  SOURCE_TYPE.CISCO_SECURE_ENDPOINT,
  SOURCE_TYPE.LOGMEIN_CENTRAL,
  SOURCE_TYPE.CISCO_MERAKI,
  SOURCE_TYPE.AWS_S3,
  SOURCE_TYPE.GOOGLE_STORAGE,
  SOURCE_TYPE.THINKST_CANARY,
  SOURCE_TYPE.ACTION1
]);

/**
 * Response model for checking if setup guide PDF exists.
 * Used to conditionally show "Download Setup Guide" button.
 */
export type PdfFileExistsModel = {
  exists: boolean;
};
